import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, UserPlus, GraduationCap } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { parseApiError } from '../../lib/apiErrors';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/9i39bysf_475444528_1384443796015613_5677894820733752221_n%20%281%29.jpg';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Trim email + lowercase to avoid silent backend rejection
    const email = (formData.email || '').trim().toLowerCase();
    const nom = (formData.nom || '').trim();
    const prenom = (formData.prenom || '').trim();

    // Client-side validation : clearer errors than Pydantic's array shape
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({ title: 'Email invalide', description: 'Veuillez saisir un email valide (ex: nom@exemple.fr).', variant: 'destructive' });
      return;
    }
    if (!nom || !prenom) {
      toast({ title: 'Nom et prénom requis', description: 'Merci de remplir votre nom et prénom.', variant: 'destructive' });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast({ title: 'Mot de passe', description: 'Les mots de passe ne correspondent pas', variant: 'destructive' });
      return;
    }
    if (formData.password.length < 6) {
      toast({ title: 'Mot de passe trop court', description: 'Le mot de passe doit contenir au moins 6 caractères', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const result = await register({
        nom,
        prenom,
        email,
        password: formData.password,
        role: 'parent'
      });
      if (result?.pending_approval) {
        toast({
          title: '✓ Compte créé — En attente de validation',
          description: "Votre compte a été enregistré. Un administrateur le validera prochainement. Vous serez prévenu(e) par email dès qu'il sera activé.",
        });
        navigate('/login?pending=1');
        return;
      }
      toast({ title: 'Compte créé', description: 'Votre compte a été créé avec succès !' });
      navigate('/espace-apprenant');
    } catch (error) {
      toast({ 
        title: 'Inscription impossible', 
        description: parseApiError(error),
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#285BBB] to-[#2C3C65] flex items-center justify-center p-4" data-testid="register-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="The English Speaking Center" 
              className="h-16 mx-auto mb-4"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center justify-center text-[#285BBB] font-bold text-2xl mb-4">
              <GraduationCap className="h-10 w-10 mr-2" />
              <span>The English Speaking Center</span>
            </div>
            <h1 className="text-2xl font-bold text-[#2C3C65]">Créer un compte</h1>
            <p className="text-[#6777A1] mt-2">Rejoignez notre communauté</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">Nom</label>
                <Input
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  required
                  data-testid="register-nom"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">Prénom</label>
                <Input
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  required
                  data-testid="register-prenom"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-1">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-1">Mot de passe</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-400 hover:text-[#285BBB] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#285BBB] z-10 transition-colors"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  data-testid="register-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-1">Confirmer le mot de passe</label>
              <Input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                data-testid="register-confirm-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#285BBB] hover:bg-[#2C3C65] text-white py-3 mt-6"
              data-testid="register-submit"
            >
              {loading ? 'Création...' : (
                <>
                  <UserPlus className="h-5 w-5 mr-2" />
                  Créer mon compte
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#6777A1]">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-[#285BBB] font-medium hover:underline">
                Se connecter
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-[#6777A1] hover:text-[#285BBB]">
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
